import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { MoreHorizontal } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

export interface OverflowTabItem {
  value: string;
  label: string;
  badge?: React.ReactNode;
  menuBadge?: React.ReactNode;
}

interface OverflowTabsListProps {
  items: OverflowTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  triggerClassName: string;
  className?: string;
}

const GAP_PX = 4;
const MORE_BUTTON_PX = 28;

export const OverflowTabsList: React.FC<OverflowTabsListProps> = ({
  items,
  value,
  onValueChange,
  triggerClassName,
  className,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const measure = useCallback(() => {
    const row = rowRef.current;
    const measurer = measureRef.current;
    if (!row || !measurer) return;

    const available = row.clientWidth;
    const widths = Array.from(measurer.children).map(
      (child) => child.getBoundingClientRect().width,
    );
    if (available === 0 || widths.length === 0) return;

    const total =
      widths.reduce((sum, width) => sum + width, 0) +
      GAP_PX * (widths.length - 1);

    if (total <= available) {
      setVisibleCount(widths.length);
      return;
    }

    const budget = available - MORE_BUTTON_PX - GAP_PX;
    let used = 0;
    let fits = 0;
    // The row keeps a prefix of the tabs, so counting stops at the first one
    // that does not fit rather than skipping it for a shorter label later on.
    for (const width of widths) {
      const next = used + width + (fits === 0 ? 0 : GAP_PX);
      if (next > budget) break;
      used = next;
      fits += 1;
    }
    setVisibleCount(Math.max(1, fits));
  }, []);

  useLayoutEffect(measure, [measure, items]);

  useEffect(() => {
    const row = rowRef.current;
    const measurer = measureRef.current;
    if (!row || !measurer) return;

    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(measurer);
    return () => observer.disconnect();
  }, [measure]);

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);
  const isActiveHidden = hiddenItems.some((item) => item.value === value);
  const hasHiddenBadge = hiddenItems.some(
    (item) => item.badge ?? item.menuBadge,
  );

  return (
    <div className={cn('p-2', className)}>
      <div ref={rowRef} className='relative flex min-w-0 items-center'>
        <TabsList className='flex h-auto w-full min-w-0 flex-nowrap items-center justify-start gap-1 rounded-lg bg-transparent p-0'>
          {visibleItems.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className={cn(triggerClassName, item.badge && 'relative')}
            >
              {item.label}
              {item.badge}
            </TabsTrigger>
          ))}

          {hiddenItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type='button'
                  aria-label='More tabs'
                  className={cn(
                    triggerClassName,
                    'relative inline-flex size-7 items-center justify-center px-0',
                    isActiveHidden && 'bg-muted text-foreground',
                  )}
                >
                  <MoreHorizontal className='size-4' />
                  {hasHiddenBadge && (
                    <span className='absolute -top-1 -right-1 size-2 animate-pulse rounded-full bg-red-500' />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {hiddenItems.map((item) => (
                  <DropdownMenuItem
                    key={item.value}
                    onSelect={() => onValueChange(item.value)}
                    className={cn(
                      'gap-2',
                      item.value === value && 'bg-muted text-foreground',
                    )}
                  >
                    <span className='flex-1'>{item.label}</span>
                    {item.menuBadge}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TabsList>

        {/* Off-screen copy of every tab: the row only ever renders the ones
            that fit, so widths have to be read from somewhere unclipped. */}
        <div
          ref={measureRef}
          aria-hidden
          className='pointer-events-none invisible absolute top-0 left-0 flex flex-nowrap gap-1'
        >
          {items.map((item) => (
            <span
              key={item.value}
              className={cn(
                triggerClassName,
                'inline-flex items-center border border-transparent whitespace-nowrap',
              )}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
